"use client";
import React, { useState } from "react";
import Sidebar from "@/components/shared/shell/Sidebar";
import Header from "@/components/shared/shell/Header";
import { useAuth } from "@/lib/useAuth";
import { SchoolProvider, useSchool } from "@/lib/useSchool";
import { useLocale } from "next-intl";
import { usePathname } from "next/navigation";
import { Box, CircularProgress } from "@mui/material";
import { useEffectOnce } from "@/lib/useEffectOnce";
import { checkIfInSchoolContext } from "@/lib/url-utils";
import VideoPlayerProvider from "@/lib/useVideoPlayer";
import { useNavigation } from "@/lib/navigation";
// Centralized constants for consistent spacing
const HEADER_HEIGHT = 64; // Height in pixels for header and related spacing

function isPublicRoute(pathname: string) {
  // Matches /, /login, /en, /he, /en/login, /he/login, etc.
  return (
    /^\/(|login)$/.test(pathname) ||
    /^\/[a-zA-Z-]+\/?$/.test(pathname) ||
    /^\/[a-zA-Z-]+\/login\/?$/.test(pathname)
  );
}

function SchoolAwareLayout({ children }: { children: React.ReactNode }) {
  const { inSchoolContext, schoolLoading } = useSchool();
  const { user, loading: authLoading } = useAuth();
  const locale = useLocale();
  const isRTL = locale === "he";
  const dir = isRTL ? "rtl" : "ltr";
  const pathname = usePathname();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [handoffWait, setHandoffWait] = useState(false);
  const [compactSidebar, setCompactSidebar] = useState(true);

  // Only use Firebase user for logged-in state
  const isLoggedIn = !!user;
  useEffectOnce(() => {
    const compact = localStorage.getItem("compactSidebar");
    if (compact === "false") {
      setCompactSidebar(false);
      setSidebarOpen(true);
    } else {
      setCompactSidebar(true);
    }
  }, []);

  // Add comprehensive logging for auth state
  useEffectOnce(() => {
    console.log("🔐 [ClientLayoutInner] Auth state update:", {
      hasUser: !!user,
      isLoggedIn,
      userId: user?.uid ? user.uid.substring(0, 8) + "..." : null,
      userEmail: user?.email,
      authLoading,
      schoolLoading,
      currentPath: pathname,
      timestamp: new Date().toISOString(),
    });
  }, [user, isLoggedIn, authLoading, schoolLoading, pathname]);

  const loading = authLoading || schoolLoading;

  // Briefly wait on school subdomains if a handoff is in progress so we don't render blank content
  useEffectOnce(() => {
    if (typeof document === "undefined") return;
    if (user) {
      setHandoffWait(false);
      return;
    }
    if (inSchoolContext) {
      const cookies = document.cookie || "";
      const hasCustomToken = cookies.includes("firebaseCustomToken=");
      const hasSchoolSession = cookies.includes("schoolSession=");
      if (hasCustomToken || hasSchoolSession) {
        setHandoffWait(true);
        const t = setTimeout(() => setHandoffWait(false), 4000);
        return () => clearTimeout(t);
      }
    }
  }, [inSchoolContext, user]);

  if (loading) {
    console.log("⏳ [ClientLayoutInner] Loading state - showing spinner");
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const toggleCompactSidebar = () => {
    if (isLoggedIn) {
      if (sidebarOpen) {
        setSidebarOpen(false);
        localStorage.setItem("compactSidebar", "true");
        setCompactSidebar(true);
      } else {
        localStorage.setItem("compactSidebar", "false");
        setSidebarOpen(true);
      }
    }
    setCompactSidebar(!compactSidebar);
  };

  // Always show consistent Header and Sidebar layout when user is logged in
  return (
    <Box dir={dir} display="flex" flexDirection="column" height="100vh">
      {/* Header - always show the main Header component */}
      <Header
        openSidebar={toggleCompactSidebar}
        headerHeight={HEADER_HEIGHT}
        showSidebarButton={isLoggedIn}
        compactSidebar={compactSidebar}
      />

      {/* Main Layout with Sidebar and Content */}
      <Box display="flex" flexDirection="row" flex={1}>
        {/* Sidebar - only show when user is logged in */}
        {isLoggedIn && (
          <Sidebar
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            onMouseEnter={() => {
              if (compactSidebar) return;
              setSidebarOpen(true);
            }}
            onMouseLeave={() => {
              if (compactSidebar) {
                setSidebarOpen(false);
              }
            }}
            headerHeight={HEADER_HEIGHT}
          />
        )}
        {/* Main Content */}
        <Box
          id="main-content-wrapper"
          component="main"
          flex={1}
          overflow="auto"
          sx={{ mt: HEADER_HEIGHT / 8 }}
        >
          {!user && !isPublicRoute(pathname) ? (
            inSchoolContext && handoffWait ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "calc(100vh - " + HEADER_HEIGHT + "px)",
                }}
              >
                <CircularProgress />
              </Box>
            ) : null
          ) : (
            children
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default function ClientLayoutInner({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user, loading } = useAuth();
  const pathname = usePathname();
  const navigation = useNavigation();

  // Centralized auth check: redirect unauthorized users from protected routes
  useEffectOnce(() => {
    if (loading) return; // Wait for auth to load

    // Check if we're in a school context
    const inSchoolContext =
      typeof window !== "undefined" &&
      checkIfInSchoolContext(window.location.host);

    if (!user && !isPublicRoute(pathname) && !inSchoolContext) {
      navigation.navigateTo(`/`, { method: "router.replace" });
    }
  }, [user, loading, pathname]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <SchoolProvider>
      <VideoPlayerProvider>
        <SchoolAwareLayout>{children}</SchoolAwareLayout>
      </VideoPlayerProvider>
    </SchoolProvider>
  );
}
